import Button from "@Components/Button";
import { AppState } from "@Store/index";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropertyDetailsModal from "../PropertyDetailsModal";
import modalContainerStyles from "../PropertyDetailsModal/PropertyDetailsModal.module.scss";
import styles from "./AmenitiesSectionStyles.module.scss";
import AmenitySection from "./components/AmenitySection";
import getTotalAmenitiesCount from "./utils/helper";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import BillsIncludedModal from "./components/BillsIncludedModal";
import amenityStyles from "./components/AmenitySectionStyles.module.scss";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import config from "@Config/index";
import { getTopAmenitySections, sortAmenitySections } from "../PropertyDetailsSection/helper";
// import {
//   getPricingCurrency,
//   getPricingDuration,
// } from "@Pages/PropertyPage/components/RoomTypeSection/utils";

const AmenitiesSection = ({ isDesktop = false }) => {
  const features = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData?.data?.features,
  );
  const { t } = useTranslationHook();
  const [showModal, setShowModal] = useState(false);
  const featuresData = features?.filter((feature: any) => feature.values?.length > 0) || [];
  const sortedData = sortAmenitySections(featuresData, true);
  // Show top priority amenity section(s) at top level
  const hasBills =
    (useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BILLS_INCLUDED) || config?.IS_CHINA_APP) &&
    sortedData?.some(
      (amenity: any) =>
        (amenity.name_en || amenity.name) === "Bills Included" && amenity.values?.length > 0,
    );
  const limit = hasBills && !isDesktop ? 2 : 3;
  const topAmenitySections = getTopAmenitySections(sortedData, limit, true);
  const getFilteredData = () => {
    if (topAmenitySections.length === 0) return [];
    if (hasBills) return [...topAmenitySections];
    return (
      sortedData?.filter((a: any) => (a.name_en || a.name) !== "Bills Included").slice(0, 3) ||
      topAmenitySections
    );
  };
  const filteredData = getFilteredData();
  const otherAmenitiesData = sortedData?.filter((amenity: any) => !filteredData.includes(amenity));
  const otherAmenitiesWithoutBills = otherAmenitiesData?.filter(
    (amenity: any) => (amenity.name_en || amenity.name) !== "Bills Included",
  );
  const totalAmenities = getTotalAmenitiesCount(sortedData);

  const showBillsIncluded =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BILLS_INCLUDED) || config?.IS_CHINA_APP;

  const billsIncludedFeature = useMemo(
    () =>
      sortedData?.find((amenity: any) => (amenity.name_en || amenity.name) === "Bills Included") ||
      null,
    [JSON.stringify(sortedData)],
  );

  const billsAmenities = billsIncludedFeature?.values || [];
  const useFourColumnGrid = billsAmenities.length > 6;

  // Determine narrow panel: 650px when every section has ≤2 amenity items
  const otherSectionsMaxItems = Math.max(
    ...[...filteredData, ...otherAmenitiesWithoutBills].map(
      (section: any) => section.values?.length || 0,
    ),
    0,
  );
  const billsItemCount = (showBillsIncluded && billsAmenities.length) || 0;
  const useNarrowPanel = billsItemCount <= 2 && otherSectionsMaxItems <= 2;

  // const currencySymbol = getPricingCurrency(inventoryCurrency) || "£";
  // const pricingPeriod = getPricingDuration(inventoryPricingDuration) || "week";

  const totalCalculatedPrice = billsAmenities.reduce((sum: number, amenity: any) => {
    const price = Number(amenity.price);
    const fixedPrice = price && !Number.isNaN(price) ? parseFloat(price.toFixed(1)) : 0;
    return sum + fixedPrice;
  }, 0);

  // const hasEstimatedItems = billsAmenities.some((amenity: any) => {
  //   const price = Number(amenity.price);
  //   return !price || Number.isNaN(price);
  // });

  const handleModalOpen = () => {
    gtagEventEmitter({
      action: "viewed_amenities",
      category: PAGE.INVENTORY,
      label: "success",
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const viewAllText = t("propertypage.amenitiessection.viewallamenitiesbutton");

  const renderOtherAmenities = () => (
    <div
      className={formatClassNames(
        styles.amenitiesContainerInnerHidden,
        isDesktop && styles.desktop,
      )}
    >
      {otherAmenitiesData?.map((feature: any, idx: number) => (
        <AmenitySection
          dataTestId="property-amentities-other"
          title={feature.name}
          titleEn={feature.name_en}
          amenityList={feature.values}
          withDivider={idx < otherAmenitiesData.length - 1}
          key={feature.name_en || feature.name}
          isDesktop={isDesktop}
          amenitiesImages={[]}
        />
      ))}
    </div>
  );

  return (
    <>
      <div
        className={formatClassNames(styles.propertyFeaturesContainer, isDesktop && styles.desktop)}
        id="amenities"
      >
        <h2
          data-testid="property-amenities"
          className={formatClassNames(styles.amenitiesTitle, isDesktop && styles.desktop)}
        >
          {t("propertypage.amenitiessection.amenitiestitle")}
        </h2>
        <div className={styles.amenitiesContainerInner}>
          {showBillsIncluded && billsAmenities.length > 0 && (
            <div className={formatClassNames(amenityStyles.container)}>
              <h3
                data-testid="property-amenities-section-header"
                className={formatClassNames(
                  amenityStyles.heading,
                  isDesktop && amenityStyles.desktop,
                )}
              >
                {t("propertypage.amenitiessection.amenitysection.billsincluded")}{" "}
                <span
                  className={formatClassNames(
                    amenityStyles.headingLight,
                    isDesktop && amenityStyles.desktop,
                  )}
                >
                  {t("propertypage.components.amenitiessection.inrent")}
                </span>
              </h3>
              <BillsIncludedModal
                dataTestId="property-amentities"
                amenities={billsAmenities}
                totalWeeklyCost={totalCalculatedPrice}
                isDesktop={isDesktop}
                useFourColumnGrid={useFourColumnGrid}
              />
            </div>
          )}
          {showBillsIncluded && billsAmenities.length > 0 && filteredData.length > 0 && (
            <div className={styles.dividerDashed} />
          )}
          {filteredData.map((feature: any, idx: number) => (
            <AmenitySection
              dataTestId="property-amentities"
              title={feature.name}
              titleEn={feature.name_en}
              amenityList={feature.values}
              withDivider={idx < filteredData.length - 1}
              key={feature.name_en || feature.name}
              isDesktop={isDesktop}
              // amenitiesImages={amenitiesImages?.slice(0, 5) || []}
              amenitiesImages={[]} // Disable amenities images
            />
          ))}
          {((showBillsIncluded && billsAmenities.length > 0) || filteredData.length > 0) && (
            <div className={styles.divider} />
          )}

          <Button
            dataTestId="property-view-all-amenities"
            variant="showAll"
            onClick={handleModalOpen}
            labelStyles={styles.viewAllButtonWrapper}
            className={styles.viewAllAmenitiesButton}
            isDesktop={isDesktop}
          >
            {viewAllText} ({totalAmenities})
          </Button>
        </div>
        {!showModal && renderOtherAmenities()}
      </div>
      {showModal && (
        <PropertyDetailsModal
          header={t("propertypage.amenitiessection.amenitiestitle")}
          hidePropertyModal={handleModalClose}
          isDesktop={isDesktop}
          bodyWithNoPadding
          sidePanel
          removeFooter={isDesktop}
          modalClassName={
            isDesktop && useNarrowPanel ? modalContainerStyles.sidePanelContainerNarrow : ""
          }
        >
          <div className={styles.amenitiesContainerOuter}>
            <div className={styles.amenitiesContainerInner}>
              {showBillsIncluded && billsAmenities.length > 0 && (
                <div
                  className={formatClassNames(
                    amenityStyles.container,
                    amenityStyles.modalView,
                    amenityStyles.modalViewNoTopPadding,
                    filteredData.length === 0 &&
                      otherAmenitiesWithoutBills.length === 0 &&
                      amenityStyles.modalViewNoBottomPadding,
                  )}
                >
                  <h3
                    data-testid="property-amenities-section-header"
                    className={formatClassNames(
                      amenityStyles.heading,
                      isDesktop && amenityStyles.desktop,
                    )}
                  >
                    {t("propertypage.amenitiessection.amenitysection.billsincluded")}{" "}
                    <span
                      className={formatClassNames(
                        amenityStyles.headingLight,
                        isDesktop && amenityStyles.desktop,
                      )}
                    >
                      {t("propertypage.components.amenitiessection.inrent")}
                    </span>
                  </h3>
                  <BillsIncludedModal
                    dataTestId="property-amentities-modal"
                    amenities={billsAmenities}
                    totalWeeklyCost={totalCalculatedPrice}
                    isDesktop={isDesktop}
                    useFourColumnGrid={useFourColumnGrid}
                  />
                </div>
              )}

              {showBillsIncluded &&
                billsAmenities.length > 0 &&
                (filteredData.length > 0 || otherAmenitiesWithoutBills.length > 0) && (
                  <div className={styles.divider} />
                )}

              {filteredData.map((feature: any, idx: number) => (
                <AmenitySection
                  dataTestId="property-amentities-modal"
                  title={feature.name}
                  titleEn={feature.name_en}
                  amenityList={feature.values}
                  withDivider={idx < filteredData.length - 1}
                  isDesktop={isDesktop}
                  amenitiesImages={[]}
                  modalView
                  showAll
                  isFirstSectionInModal={
                    !(showBillsIncluded && billsAmenities.length > 0) && idx === 0
                  }
                  isLastSectionInModal={
                    otherAmenitiesWithoutBills.length === 0 && idx === filteredData.length - 1
                  }
                />
              ))}

              {filteredData.length > 0 && otherAmenitiesWithoutBills.length > 0 && (
                <div className={styles.divider} />
              )}

              {otherAmenitiesWithoutBills.map((feature: any, idx: number) => (
                <AmenitySection
                  dataTestId="property-amentities-modal"
                  title={feature.name}
                  titleEn={feature.name_en}
                  amenityList={feature.values}
                  withDivider={idx < otherAmenitiesWithoutBills.length - 1}
                  isDesktop={isDesktop}
                  amenitiesImages={[]}
                  modalView
                  showAll
                  isLastSectionInModal={idx === otherAmenitiesWithoutBills.length - 1}
                />
              ))}
            </div>
          </div>
        </PropertyDetailsModal>
      )}
    </>
  );
};

export default wrapperHOC(AmenitiesSection, {
  componentName: "AmenitiesSection-PropertyPage",
  showForChina: true,
});
